// ===========================================
// DIAGNOSTIC LOGGER - 4-LAYER EVENT TRACING
// ===========================================
//
// Captures the complete event flow for debugging:
//   Layer 1: ACTIONS - User/autoplay initiated actions
//   Layer 2: SENT    - Server events sent (via Supabase)
//   Layer 3: RECV    - Client events received
//   Layer 4: ANIM    - Animations visualized
//
// Usage:
//   import { diagLog, DiagnosticLayer } from './diagnosticLogger';
//   diagLog.action({ seat: 2, action: 'HIT', source: 'AUTOPLAY' });
//   diagLog.recv({ seq: 15, type: 'ACTION_TAKEN', seat: 2 });
//   diagLog.anim({ id: 'card-deal-1', type: 'CARD_DEALT', phase: 'START' });
//
// View logs:
//   window.diagLog.dump()      - Print all logs to console
//   window.diagLog.export()    - Get logs as JSON string
//   window.diagLog.clear()     - Clear log buffer
//   window.diagLog.enable()    - Enable logging
//   window.diagLog.disable()   - Disable logging
//
// ===========================================

export type DiagnosticLayer = 'ACTION' | 'SENT' | 'RECV' | 'ANIM';

export interface DiagnosticEntry {
  layer: DiagnosticLayer;
  timestamp: number;
  relativeMs: number;  // ms since logging started
  data: Record<string, unknown>;
  formatted: string;   // Pre-formatted string for easy reading
}

// Configuration
const MAX_LOG_ENTRIES = 100000;  // Rolling buffer size
const LOG_TO_CONSOLE = true;   // Also log to browser console

class DiagnosticLogger {
  private entries: DiagnosticEntry[] = [];
  private startTime: number = Date.now();
  private enabled: boolean = true;
  private roundNumber: number = 0;
  private roundStartTime: number = Date.now();

  // ===========================================
  // LAYER 1: ACTIONS INITIATED
  // ===========================================

  /**
   * Log a user or autoplay action
   */
  action(data: {
    seat?: number;
    action: string;
    source: 'USER' | 'AUTOPLAY' | 'SYSTEM';
    details?: Record<string, unknown>;
  }): void {
    this.log('ACTION', {
      seat: data.seat,
      action: data.action,
      source: data.source,
      ...data.details,
    });
  }

  /**
   * Log a bet action
   */
  bet(data: {
    seat: number;
    amount: number;
    source: 'USER' | 'AUTOPLAY' | 'AUTOBET';
    type: 'PLACE' | 'CLEAR' | 'REBET' | 'DOUBLE';
  }): void {
    this.log('ACTION', {
      seat: data.seat,
      action: `BET_${data.type}`,
      amount: data.amount,
      source: data.source,
    });
  }

  /**
   * Log a sidebet action
   */
  sidebet(data: {
    seat: number;
    sidebetType: string;
    amount: number;
    action: 'PLACE' | 'REMOVE';
    source: 'USER' | 'AUTOPLAY';
  }): void {
    this.log('ACTION', {
      seat: data.seat,
      action: `SIDEBET_${data.action}`,
      sidebetType: data.sidebetType,
      amount: data.amount,
      source: data.source,
    });
  }

  // ===========================================
  // LAYER 2: SERVER EVENTS SENT
  // (Logged server-side to Supabase, but we can
  //  also log client-sent messages here)
  // ===========================================

  /**
   * Log a message sent TO the server
   */
  sent(data: {
    type: string;
    details?: Record<string, unknown>;
  }): void {
    this.log('SENT', {
      type: data.type,
      ...data.details,
    });
  }

  // ===========================================
  // LAYER 3: CLIENT EVENTS RECEIVED
  // ===========================================

  /**
   * Log an event received from server
   */
  recv(data: {
    seq: number;
    type: string;
    seat?: number;
    hand?: number;
    details?: Record<string, unknown>;
  }): void {
    this.log('RECV', {
      seq: data.seq,
      type: data.type,
      seat: data.seat,
      hand: data.hand,
      ...data.details,
    });
  }

  /**
   * Log a batch of events received
   */
  recvBatch(data: {
    count: number;
    types: string[];
    seqRange: [number, number];
  }): void {
    this.log('RECV', {
      batch: true,
      count: data.count,
      types: data.types.join(','),
      seqRange: `${data.seqRange[0]}-${data.seqRange[1]}`,
    });
  }

  /**
   * Log event skipped as duplicate
   */
  recvSkip(data: {
    seq: number;
    type: string;
    reason: string;
  }): void {
    this.log('RECV', {
      seq: data.seq,
      type: data.type,
      SKIPPED: true,
      reason: data.reason,
    });
  }

  // ===========================================
  // LAYER 4: ANIMATIONS VISUALIZED
  // ===========================================

  /**
   * Log animation start
   */
  animStart(data: {
    id: string;
    type: string;
    seat?: number;
    hand?: number;
    details?: Record<string, unknown>;
  }): void {
    this.log('ANIM', {
      phase: 'START',
      id: data.id,
      type: data.type,
      seat: data.seat,
      hand: data.hand,
      ...data.details,
    });
  }

  /**
   * Log animation end
   */
  animEnd(data: {
    id: string;
    type: string;
    durationMs?: number;
    seat?: number;
    hand?: number;
  }): void {
    this.log('ANIM', {
      phase: 'END',
      id: data.id,
      type: data.type,
      durationMs: data.durationMs,
      seat: data.seat,
      hand: data.hand,
    });
  }

  /**
   * Log animation timeout/failure
   */
  animTimeout(data: {
    id: string;
    type: string;
    waitedMs: number;
  }): void {
    this.log('ANIM', {
      phase: 'TIMEOUT',
      id: data.id,
      type: data.type,
      waitedMs: data.waitedMs,
    });
  }

  // ===========================================
  // LAYER 5: UI STATE CHANGES
  // ===========================================

  /**
   * Log UI element visibility changes (modals, badges, panels)
   */
  ui(data: {
    element: string;
    action: 'SHOW' | 'HIDE';
    seat?: number;
    source?: 'USER' | 'AUTOPLAY' | 'SYSTEM' | 'TIMEOUT';
    details?: Record<string, unknown>;
  }): void {
    this.log('ACTION', {
      ui: true,
      element: data.element,
      action: data.action,
      seat: data.seat,
      source: data.source,
      ...data.details,
    });
  }

  // ===========================================
  // ROUND TRACKING
  // ===========================================

  /**
   * Mark the start of a new round (resets relative timing within round)
   */
  newRound(roundNumber: number): void {
    this.roundNumber = roundNumber;
    this.roundStartTime = Date.now();
    this.log('ACTION', {
      action: 'ROUND_START',
      round: roundNumber,
      source: 'SYSTEM',
    });
  }

  // ===========================================
  // PANIC BUTTON
  // ===========================================

  /**
   * Insert a PANIC BUTTON marker into all 4 layers.
   * Use this to mark notable moments for later analysis.
   */
  panic(note?: string): void {
    const marker = '======== PANIC BUTTON ========';
    const data = { PANIC: true, note: note || 'User marked this moment' };

    // Insert into all 4 layers for easy filtering
    this.log('ACTION', { ...data, marker });
    this.log('SENT', { ...data, marker });
    this.log('RECV', { ...data, marker });
    this.log('ANIM', { ...data, marker });

    // Also log prominently to console
    console.log('%c' + marker, 'background: #ff0000; color: #ffffff; font-size: 16px; font-weight: bold; padding: 4px 8px;');
    if (note) console.log('%cNote: ' + note, 'color: #ff0000; font-weight: bold;');
  }


  // ===========================================
  // CORE LOGGING
  // ===========================================

  private log(layer: DiagnosticLayer, data: Record<string, unknown>): void {
    if (!this.enabled) return;

    const now = Date.now();
    const relativeMs = now - this.roundStartTime;
    const globalMs = now - this.startTime;

    // Build formatted string
    const layerPad = layer.padEnd(6);
    const timePad = `${relativeMs}ms`.padStart(8);
    const roundPad = `R${this.roundNumber}`.padStart(4);

    // Format data compactly
    const dataStr = Object.entries(data)
      .filter(([_, v]) => v !== undefined)
      .map(([k, v]) => `${k}=${typeof v === 'object' ? JSON.stringify(v) : v}`)
      .join(' ');

    const formatted = `[${layerPad}] ${roundPad} ${timePad} ${dataStr}`;

    const entry: DiagnosticEntry = {
      layer,
      timestamp: now,
      relativeMs,
      data: { ...data, round: this.roundNumber, globalMs },
      formatted,
    };

    // Add to buffer (rolling)
    this.entries.push(entry);
    if (this.entries.length > MAX_LOG_ENTRIES) {
      this.entries.shift();
    }

    // Also log to console if enabled
    if (LOG_TO_CONSOLE) {
      const color = this.getLayerColor(layer);
      console.log(`%c${formatted}`, `color: ${color}; font-family: monospace;`);
    }
  }

  private getLayerColor(layer: DiagnosticLayer): string {
    switch (layer) {
      case 'ACTION': return '#FF9800';  // Orange
      case 'SENT':   return '#2196F3';  // Blue
      case 'RECV':   return '#4CAF50';  // Green
      case 'ANIM':   return '#9C27B0';  // Purple
    }
  }

  // ===========================================
  // EXPORT & ANALYSIS
  // ===========================================

  /**
   * Dump all logs to console in formatted table
   */
  dump(): void {
    console.log('\n========== DIAGNOSTIC LOG DUMP ==========\n');

    // Group by round
    const byRound = new Map<number, DiagnosticEntry[]>();
    for (const entry of this.entries) {
      const round = (entry.data.round as number) || 0;
      if (!byRound.has(round)) byRound.set(round, []);
      byRound.get(round)!.push(entry);
    }

    for (const [round, entries] of byRound) {
      console.log(`\n----- ROUND ${round} (${entries.length} entries) -----\n`);
      for (const entry of entries) {
        console.log(entry.formatted);
      }
    }

    console.log('\n========== END DUMP ==========\n');
  }

  /**
   * Export logs as JSON
   */
  export(): string {
    return JSON.stringify(this.entries, null, 2);
  }

  /**
   * Export logs as compact text (for pasting)
   */
  exportText(): string {
    return this.entries.map(e => e.formatted).join('\n');
  }

  /**
   * Get summary statistics
   */
  summary(): {
    totalEntries: number;
    byLayer: Record<DiagnosticLayer, number>;
    byRound: Record<number, number>;
    anomalies: string[];
  } {
    const byLayer: Record<DiagnosticLayer, number> = {
      ACTION: 0, SENT: 0, RECV: 0, ANIM: 0
    };
    const byRound: Record<number, number> = {};
    const anomalies: string[] = [];

    for (const entry of this.entries) {
      byLayer[entry.layer]++;
      const round = (entry.data.round as number) || 0;
      byRound[round] = (byRound[round] || 0) + 1;
    }

    // Detect anomalies
    // 1. More ANIMs than RECVs for same type
    const recvByType = new Map<string, number>();
    const animByType = new Map<string, number>();

    for (const entry of this.entries) {
      const type = entry.data.type as string;
      if (!type) continue;

      if (entry.layer === 'RECV') {
        recvByType.set(type, (recvByType.get(type) || 0) + 1);
      }
      if (entry.layer === 'ANIM' && entry.data.phase === 'START') {
        animByType.set(type, (animByType.get(type) || 0) + 1);
      }
    }

    for (const [type, animCount] of animByType) {
      const recvCount = recvByType.get(type) || 0;
      if (animCount > recvCount) {
        anomalies.push(`${type}: ${animCount} animations but only ${recvCount} events received`);
      }
    }

    return { totalEntries: this.entries.length, byLayer, byRound, anomalies };
  }

  /**
   * Analyze a specific round for issues
   */
  analyzeRound(roundNumber: number): void {
    const roundEntries = this.entries.filter(e => e.data.round === roundNumber);

    console.log(`\n========== ROUND ${roundNumber} ANALYSIS ==========\n`);
    console.log(`Total entries: ${roundEntries.length}`);

    // Timeline
    console.log('\nTimeline:');
    for (const entry of roundEntries) {
      console.log(entry.formatted);
    }

    // Check for issues
    console.log('\nIssue Check:');

    // Count by layer
    const layers = { ACTION: 0, SENT: 0, RECV: 0, ANIM: 0 };
    for (const entry of roundEntries) {
      layers[entry.layer]++;
    }
    console.log('  By layer:', layers);

    // Check ANIM START vs END matching
    const animStarts = new Set<string>();
    const animEnds = new Set<string>();
    for (const entry of roundEntries) {
      if (entry.layer === 'ANIM') {
        const id = entry.data.id as string;
        if (entry.data.phase === 'START') animStarts.add(id);
        if (entry.data.phase === 'END') animEnds.add(id);
      }
    }
    const unfinished = [...animStarts].filter(id => !animEnds.has(id));
    if (unfinished.length > 0) {
      console.log('  UNFINISHED ANIMATIONS:', unfinished);
    }

    console.log('\n========== END ANALYSIS ==========\n');
  }

  // ===========================================
  // CONTROL
  // ===========================================

  enable(): void {
    this.enabled = true;
    console.log('[DiagnosticLogger] Enabled');
  }

  disable(): void {
    this.enabled = false;
    console.log('[DiagnosticLogger] Disabled');
  }

  clear(): void {
    this.entries = [];
    this.startTime = Date.now();
    this.roundStartTime = Date.now();
    this.roundNumber = 0;
    console.log('[DiagnosticLogger] Cleared');
  }

  isEnabled(): boolean {
    return this.enabled;
  }

  getEntries(): DiagnosticEntry[] {
    return [...this.entries];
  }

  getRoundEntries(roundNumber: number): DiagnosticEntry[] {
    return this.entries.filter(e => e.data.round === roundNumber);
  }
}

// Singleton instance
export const diagLog = new DiagnosticLogger();

// Expose to window for console access
if (typeof window !== 'undefined') {
  (window as any).diagLog = diagLog;
}
